import { CheckBox } from "@mui/icons-material";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { AUFlagIcons, CloseDialogIcon } from "assets/styles/icons";
import DialogMultipleLoans from "components/apply-loan/DialogMultipleLoans";
import { getLoanTypeValue, useProgress } from "context/ProgressProvider";
import { Formik } from "formik";
import { useEffect, useState } from "react";
import OTPInput from "react-otp-input";
import { connect, useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { AuthenticationService } from "services/AuthenticationService";
import { LoanService } from "services/LoanService";
import {
  AppFormikTextField,
  AppPrimaryButton,
  AppPrimaryButtonOutlined,
  AppSecondaryButton,
} from "shared/components/AppComponents";
import { LoanType, RoleCode } from "shared/helpers/Constants";
import { RegisterSchema } from "shared/helpers/Schema";
import Toast from "shared/helpers/Toast";
import Utils from "shared/helpers/Utils";
import { AuthActions } from "state/auth/Action";
import { ProfileActions } from "state/profile/Action";
import { ProgressActions } from "state/progress/Action";

export const handleUpdateAccount = (ev, formik, callback) => {
  AuthenticationService.updateUser({
    ...ev,
    phoneNumber: ev.phoneNumber.replace(/\s+/g, ""),
  })
    .then((response: any) => {
      callback();
    })
    .catch((error: any) => {
      Utils.handleErrorResponse(error);
    })
    .finally(() => {
      if (formik) formik.setSubmitting(false);
    });
};

const YourDetails = () => {
  const {
    handleCompleteAndNext,
    navigateToSectionStepHandler,
    handleReviewDetails,
    handleUpdateUserLoan,
    currentProfile,
    handleLoanStatus,
    isBroker,
  } = useProgress();
  const navigate = useNavigate();
  const { step: urlStep } = useParams<{ step: string }>();
  const location = useLocation();
  const hostname = window.location.hostname;

  const dispatch = useDispatch();
  const authInfo = useSelector((state: any) => state.authState);

  const currentStep = urlStep ? parseInt(urlStep, 10) : 1;

  const PAGE_LENGTH = 7;
  const PAGE_ID = "intro-details";
  const nextStep = currentStep + 1;

  const isValidStep = currentStep >= 1 && currentStep < PAGE_LENGTH;

  useEffect(() => {
    if (!isValidStep) {
      navigate(`/sbl/${PAGE_ID}/1`, { replace: true });
    }
    console.log(currentStep);
  }, [currentStep, isValidStep, navigate]);

  const [showAccountExist, setShowAccountExist] = useState(false);
  const [otp, setOTP] = useState("");
  const [hasMultipleLoans, setHasMultipleLoans] = useState(false);
  const [showMultipleLoans, setShowMultipleLoans] = useState(null);
  const [loaderMultiLoans, setLoaderMultiLoans] = useState(false);
  const [userTempData, setUserTempData] = useState(null);
  const [otpContinueLoading, setOtpContinueLoading] = useState(false);
  const [otpResendLoading, setOtpResendLoading] = useState(false);
  const [loginExistingLoading, setLoginExistingLoading] = useState(false);

  const loanType = LoanType.SBL;

  const handleRequestOTP = (email) => {
    setOtpResendLoading(true);
    AuthenticationService.requestOTP({ email: email })
      .then((response: any) => {
        Utils.handleSuccessResponse(response);
        if (
          process.env.REACT_APP_ENV === "development" ||
          hostname === "dev-rnd.ezifin.com"
        ) {
          setOTP(response.data.data.otp);
        }

        const hasLoans = new Promise((resolve, reject) => {
          dispatch(
            AuthActions.setAuthData({
              hasMultipleLoans: response.data.data?.hasMultipleLoans,
            }),
          );
          resolve(true);
        });

        hasLoans.then(() => {
          setHasMultipleLoans(response.data.data.hasMultipleLoans);
        });
      })
      .catch((error: any) => {
        Utils.handleErrorResponse(error);
      })
      .finally(() => {
        setOtpResendLoading(false);
      });
  };

  const handleCreateAccount = (ev, formik) => {
    setUserTempData(ev);
    console.log(ev);
    if (authInfo.isUserLoggedIn) {
      handleUpdateAccount(ev, formik, () => {
        dispatch(ProfileActions.setSblProfile(ev));
        handleCompleteAndNext();
      });
      return;
    }

    AuthenticationService.createUser({
      ...ev,
      phoneNumber: ev.phoneNumber.replace(/\s+/g, ""),
      loanType,
      createdByRole: RoleCode.APPLICANT,
      createdByBroker: null,
    })
      .then((response: any) => {
        Toast.showSuccessMessage(
          "We’ve sent a one-time password (OTP) to your email. Please use that to verify your email.",
        );
        navigateToSectionStepHandler(PAGE_ID, nextStep);
      })
      .catch((error: any) => {
        if (error.status === 409) {
          handleRequestOTP(ev.email);
          setShowAccountExist(true);
        }
        // Utils.handleErrorResponse(error);
      })
      .finally(() => {
        formik.setSubmitting(false);
      });
  };

  const updateUserProfile = (data) => {
    if (!data) return;
    let LoanType = getLoanTypeValue(data.loanType);

    const updateUserDeails = new Promise((resolve, reject) => {
      if (data.loanType === 0) {
        dispatch(
          ProfileActions.setSblProfile({
            email: data.email,
            isEmailVerified: data.isEmailVerified,
            loadId: data.loadId,
            userId: data.userId,
            firstName: data.firstName,
            lastName: data.lastName,
            phoneNumber: data.phoneNumber.startsWith("+61 ")
              ? data.phoneNumber
              : data.phoneNumber.startsWith("+61")
                ? "+61 " + data.phoneNumber.slice(3)
                : data.phoneNumber,
            isDocumentMappingCompleted: data.isDocumentMappingCompleted,
            identityVerificationStatus: data.identityVerificationStatus,
          }),
        );
        LoanService.getLoanReview(data.loadId)
          .then((response) => {
            handleUpdateUserLoan(response);
            handleReviewDetails(response);
          })
          .catch((error) => {
            Utils.handleErrorResponse(error);
          });
      }

      if (data.loanType === 1) {
        dispatch(
          ProfileActions.setRndProfile({
            email: data.email,
            isEmailVerified: data.isEmailVerified,
            loadId: data.loadId,
            userId: data.userId,
            firstName: data.firstName,
            lastName: data.lastName,
            phoneNumber: data.phoneNumber.startsWith("+61 ")
              ? data.phoneNumber
              : data.phoneNumber.startsWith("+61")
                ? "+61 " + data.phoneNumber.slice(3)
                : data.phoneNumber,
            isDocumentMappingCompleted: data.isDocumentMappingCompleted,
            identityVerificationStatus: data.identityVerificationStatus,
          }),
        );
        LoanService.getRNDLoanReview(data.loadId)
          .then((response) => {
            handleUpdateUserLoan(response);
            handleReviewDetails(response);
          })
          .catch((error) => {
            Utils.handleErrorResponse(error);
          });
      }
      resolve(true);
    });

    updateUserDeails.then(() => {
      setShowMultipleLoans(null);
      setLoaderMultiLoans(false);
      dispatch(ProgressActions.switchLoanType(LoanType));
    });
  };

  const handleLogin = (ev) => {
    setTimeout(() => {
      AuthenticationService.loginUser({
        loanType,
        otp: parseInt(ev, 10),
        email: userTempData.email,
      })
        .then((response: any) => {
          let data = response.data.data;

          const loginSession = new Promise((resolve, reject) => {
            dispatch(
              AuthActions.setAuthData({
                accessToken: data.accessToken,
                refreshToken: data.refreshToken,
                isUserLoggedIn: true,
              }),
            );
            resolve(true);
          });

          if (hasMultipleLoans) {
            loginSession.then(() => {
              handleLoanStatus((e) => {
                setTimeout(() => {
                  handleCloseDialog();
                  setShowMultipleLoans(data);
                  Utils.handleSuccessResponse(response);
                }, 100);
              });
            });

            return;
          }

          loginSession.then(() => {
            handleLoanStatus(() => {});
            updateUserProfile({ ...data, loadId: data.loanId });
            handleCloseDialog();
          });
          Utils.handleSuccessResponse(response);
        })
        .catch((error: any) => {
          Utils.handleErrorResponse(error);
        })
        .finally(() => {});
    }, 500);
  };

  const handleProcessOTP = (ev) => {
    setOtpContinueLoading(true);
    AuthenticationService.verifyEmail({
      otp: parseInt(ev, 10),
      email: userTempData.email,
      loanType,
    })
      .then((response: any) => {
        let data = response.data.data;
        let LoanType = getLoanTypeValue(data.loanType);

        dispatch(ProgressActions.switchLoanType(LoanType));
        dispatch(
          AuthActions.setAuthData({
            accessToken: data.accessToken,
            refreshToken: data.refreshToken,
            isUserLoggedIn: true,
            selectedLoan: data.loanId,
          }),
        );

        dispatch(
          ProfileActions.setSblProfile({
            email: data.email,
            isEmailVerified: data.isEmailVerified,
            loadId: data.loanId,
            userId: data.userId,
            firstName: data.firstName,
            lastName: data.lastName,
            phoneNumber: data.phoneNumber.startsWith("+61 ")
              ? data.phoneNumber
              : data.phoneNumber.startsWith("+61")
                ? "+61 " + data.phoneNumber.slice(3)
                : data.phoneNumber,
            kycId: data.kycId,
          }),
        );
        navigateToSectionStepHandler(PAGE_ID, nextStep);
        Utils.handleSuccessResponse(response);
        handleLoanStatus(() => {});
      })
      .catch((error: any) => {
        Utils.handleErrorResponse(error);
      })
      .finally(() => {
        setOtpContinueLoading(false);
      });
  };

  useEffect(() => {
    setOTP("");
    if (currentStep === 3) {
      setTimeout(() => {
        handleCompleteAndNext();
      }, 3000);
    }
  }, [currentStep, location.pathname]);

  const handleCloseDialog = () => {
    setShowAccountExist(false);
    setOTP("");
  };

  return (
    <>
      <Dialog open={showAccountExist} fullWidth maxWidth="sm">
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h2">Account exists</Typography>
            <IconButton onClick={() => handleCloseDialog()}>
              <CloseDialogIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Typography variant="h3" fontWeight={400}>
            Account already exists. We’ve sent a verification code to your
            email. Please check your inbox and enter the code here to continue.
          </Typography>
          <Box className="py-6 justify-center">
            <OTPInput
              value={otp}
              onChange={(ev) => setOTP(ev)}
              numInputs={6}
              renderSeparator={<span className="px-2" />}
              renderInput={(props) => <input type="number" {...props} />}
              inputStyle="otp-input-box"
              shouldAutoFocus
              inputType="number"
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ gap: "12px" }}>
          <AppSecondaryButton
            title="Cancel"
            className="btn-small"
            onClick={() => handleCloseDialog()}
            fullWidth
            disabled={loginExistingLoading}
          />
          <AppPrimaryButton
            title="Log in"
            className="btn-small"
            onClick={() => handleLogin(otp)}
            fullWidth
            isLoading={loginExistingLoading}
            loadingText="Please wait.."
            disabled={loginExistingLoading}
          />
        </DialogActions>
      </Dialog>

      {currentStep === 1 && (
        <Box>
          <Typography variant="h2">
            {isBroker
              ? "Tell us about your customer"
              : "Tell us about yourself"}
          </Typography>
          <Typography variant="h3" fontWeight={400}>
            {isBroker
              ? "Enter the customer’s personal contact details below. These details are required to create the loan application and to keep the applicant informed throughout the process."
              : "Please enter your first name, last name, email, and phone number. We’ll use these details to create your application and keep you updated."}
          </Typography>

          <Box sx={{ marginTop: "2rem" }}></Box>
          <Formik
            enableReinitialize
            validationSchema={RegisterSchema(
              authInfo?.brokerDetails?.email || null,
            )}
            validateOnMount
            onSubmit={(formValues, formik) => {
              if (currentProfile.isAllowedToEdit) {
                if (!currentProfile.isAllowedToEdit.yourDetails) {
                  handleCompleteAndNext();
                  return;
                }
              }

              formik.setSubmitting(true);
              handleCreateAccount(formValues, formik);
            }}
            initialValues={{
              firstName: currentProfile.firstName,
              lastName: currentProfile.lastName,
              email: currentProfile.email,
              phoneNumber: currentProfile.phoneNumber || "+61 ",
            }}
          >
            {({ handleSubmit, errors, isSubmitting }) => {
              return (
                <form autoComplete="off">
                  <Box>
                    <Grid container spacing={4}>
                      <Grid size={{ xs: 12, md: 6 }} component="div">
                        <AppFormikTextField
                          placeholder="First name"
                          label="First name"
                          name="firstName"
                          outerLabel
                          className="ezifin-default-field"
                        />
                      </Grid>

                      <Grid size={{ xs: 12, md: 6 }} component="div">
                        <AppFormikTextField
                          placeholder="Last name"
                          label="Last name"
                          name="lastName"
                          outerLabel
                          className="ezifin-default-field"
                        />
                      </Grid>
                    </Grid>
                  </Box>

                  <Box mt={3}>
                    <AppFormikTextField
                      placeholder="Email address"
                      label="Email address"
                      name="email"
                      outerLabel
                      className="ezifin-default-field"
                      blockedAndReadOnly={
                        authInfo.userRole !== 1 && authInfo.isUserLoggedIn
                          ? true
                          : false
                      }
                    />
                  </Box>

                  <Box mt={3}>
                    <AppFormikTextField
                      placeholder="Phone number"
                      label="Phone number"
                      name="phoneNumber"
                      outerLabel
                      className="ezifin-default-field"
                      slotProps={{
                        input: {
                          startAdornment: (
                            <InputAdornment position="start">
                              <AUFlagIcons />
                            </InputAdornment>
                          ),
                        },
                      }}
                      type="customAUPhone"
                    />
                  </Box>

                  <Box mt={3}>
                    <AppPrimaryButton
                      title="Continue"
                      isLoading={isSubmitting}
                      loadingText="Please wait.."
                      onClick={handleSubmit}
                      disabled={isSubmitting || Object.keys(errors).length > 0}
                    />
                  </Box>
                </form>
              );
            }}
          </Formik>
        </Box>
      )}

      {currentStep === 2 && (
        <Box maxWidth={"95%"}>
          <Typography variant="h2">Check your inbox</Typography>
          <Typography variant="h3" fontWeight={400}>
            We've sent a one-time password (OTP) to your email.
          </Typography>
          <Typography variant="h3" fontWeight={400}>
            Please check your inbox and enter the OTP to verify your email and
            continue your application. If you don't see the email, be sure to{" "}
            <b>check your spam or junk</b>&nbsp;folder.
          </Typography>

          <Box className="py-6">
            <OTPInput
              value={otp}
              onChange={(ev) => setOTP(ev)}
              numInputs={6}
              renderSeparator={<span className="px-2" />}
              renderInput={(props) => <input type="number" {...props} />}
              inputStyle="otp-input-box"
              shouldAutoFocus
              inputType="number"
            />
          </Box>

          <Box mt={3} sx={{ display: "flex", gap: "12px" }}>
            <AppPrimaryButton
              title="Continue"
              isLoading={otpContinueLoading}
              loadingText="Please wait.."
              onClick={() => handleProcessOTP(otp)}
              disabled={otpContinueLoading || otpResendLoading}
            />
            <AppPrimaryButtonOutlined
              title="Resend OTP"
              isLoading={otpResendLoading}
              loadingText="Please wait.."
              onClick={() => handleRequestOTP(userTempData.email)}
              className="ezifin-outlined-button"
              disabled={otpResendLoading}
            />
          </Box>
        </Box>
      )}

      {currentStep === 3 && (
        <Box>
          <Box sx={{ display: "flex", gap: "6px" }}>
            <Typography variant="h2">Email verified! </Typography>
            <CheckBox fontSize="large" sx={{ color: "#00b800" }} />
          </Box>
          <Typography variant="h3" fontWeight={400}>
            Thanks for confirming your email. Let’s continue with your
            application.
          </Typography>

          <img src="/images/email-verified.gif" width={148} height={148} />
        </Box>
      )}

      <DialogMultipleLoans
        isOpen={showMultipleLoans}
        setIsOpen={setShowMultipleLoans}
        rowLoanStatus={authInfo.rowLoanStatus}
        callback={(e) => {
          setLoaderMultiLoans(true);
          const multiLoans = new Promise((resolve, reject) => {
            dispatch(
              AuthActions.setAuthData({
                selectedLoan: e.id,
                hasMultipleLoans: true,
              }),
            );
            resolve(true);
          });
          multiLoans.then(() => {
            updateUserProfile({
              ...showMultipleLoans,
              loadId: e.id,
              loanType: e.loanActualType,
            });
          });
        }}
        loaderMultiLoans={loaderMultiLoans}
      />
    </>
  );
};

export default YourDetails;
